/* eslint-disable max-lines */
import {
  ActivityTimeframeType,
  BroadcastFeedType,
  GroupFeedThreadSortOrder,
  GroupFeedType,
  InboxFeedType,
  MessageSenderBadge,
  Reaction,
  TelemetryFeedSubtype,
  TelemetryFeedType,
  ThreadMessageLevel,
  TopicFeedType,
  TopicNetworkQuestionFeedType,
  TopicUnifiedFeedType,
  UserFeedThreadSortOrder,
  UserWallFeedThreadSortOrder,
} from '@ms/yammer-graphql';
import { Timestamp } from '@ms/yammer-protobuf';

import {
  PublisherAddTopicClickedEventProperties,
  PublisherCancelEventProperties,
  PublisherCollapsedEventProperties,
  PublisherOnPostEventProperties,
  PublisherOpenEventProperties,
  RichFormattingAnalyticsEventProperties,
  ToolbarEventProperties,
} from './publisher.types';
import {
  BaseTelemetryEvent,
  FeedLocation,
  MomentCreationType,
  PropertyCheckEventProperties,
  TelemetryEventType,
  TelemetryPropertyValueType,
} from './types';

export enum CampaignsWidgetEntrypointEnum {
  UserProfileSideBar = 'UserProfileSideBar',
  HomeFeedSideBar = 'HomeFeedSideBar',
  StorylineSideBar = 'StorylineSideBar',
  LeadersSideBar = 'LeadersSideBar',
  GroupSideBar = 'GroupSideBar',
}

export interface AnalyticsEvent extends BaseTelemetryEvent {
  readonly type: TelemetryEventType.Analytics;
  readonly properties: Record<string | keyof AnalyticsEventProperties, TelemetryPropertyValueType>;
}

export interface AnalyticsV2Event extends BaseTelemetryEvent {
  readonly type: TelemetryEventType.AnalyticsV2;
  readonly properties: AnalyticsV2EventProperties;
}

export interface AnalyticsV2EventProperties {
  [key: string]: unknown;
  readonly typeUrl: string;
  readonly value: string;
  readonly clientId: number;
  readonly ecsEtag: string;
  readonly eventId: string;
  readonly networkId: number;
  readonly timestamp:
    | {
        readonly seconds?: number | null;
        readonly nanos?: number | null;
      }
    | Timestamp;
  readonly userAgent: string;
  readonly userId: number;
  readonly webClientType: number;
}

export type AnalyticsEventProperties =
  | { [key: string]: never }
  | Pick<AnswersLinkActionAnalyticEventProperties, keyof AnswersLinkActionAnalyticEventProperties>
  | Pick<AskMeAnythingCreateClickedEventProperties, keyof AskMeAnythingCreateClickedEventProperties>
  | Pick<AskMeAnythingEventClickedEventProperties, keyof AskMeAnythingEventClickedEventProperties>
  | Pick<AskMeAnythingEventCardDisplayedProperties, keyof AskMeAnythingEventCardDisplayedProperties>
  | Pick<AskMeAnythingEventListClickedEventProperties, keyof AskMeAnythingEventListClickedEventProperties>
  | Pick<AskMeAnythingEventCreatedEventProperties, keyof AskMeAnythingEventCreatedEventProperties>
  | Pick<AutoRefreshFeedAnalyticsEventProperties, keyof AutoRefreshFeedAnalyticsEventProperties>
  | Pick<ButtonClickedAnalyticsEventProperties, keyof ButtonClickedAnalyticsEventProperties>
  | Pick<CampaignAnalyticsEventProperties, keyof CampaignAnalyticsEventProperties>
  | Pick<CampaignsHubEntryPointClickedEventProperties, keyof CampaignsHubEntryPointClickedEventProperties>
  | Pick<CampaignsHubCardClickedEventProperties, keyof CampaignsHubCardClickedEventProperties>
  | Pick<CuratedTopicAnalyticsEventProperties, keyof CuratedTopicAnalyticsEventProperties>
  | Pick<CoachingTipProperties, keyof CoachingTipProperties>
  | Pick<ConversationViewInsightsProperties, keyof ConversationViewInsightsProperties>
  | Pick<CreateCommunityClickedProperties, keyof CreateCommunityClickedProperties>
  | Pick<CreateMomentClickedProperties, keyof CreateMomentClickedProperties>
  | Pick<CommunityBadgeDisplayedProperties, keyof CommunityBadgeDisplayedProperties>
  | Pick<CommunityInsightsProperties, keyof CommunityInsightsProperties>
  | Pick<CreationFlowCreateGroupSucceededProperties, keyof CreationFlowCreateGroupSucceededProperties>
  | Pick<CreationFlowCreateCampaignSucceededProperties, keyof CreationFlowCreateCampaignSucceededProperties>
  | Pick<EditFlowEditCampaignSucceededProperties, keyof EditFlowEditCampaignSucceededProperties>
  | Pick<EmailOpenAnalyticsEventProperties, keyof EmailOpenAnalyticsEventProperties>
  | Pick<DiscoveryGroupsSortAnalyticsEventProperties, keyof DiscoveryGroupsSortAnalyticsEventProperties>
  | Pick<EmbedFeedCodeRetrievedEventProperties, keyof EmbedFeedCodeRetrievedEventProperties>
  | Pick<ExternalLinkClickedEventProperties, keyof ExternalLinkClickedEventProperties>
  | Pick<ETagMappingAnalyticsEventProperties, keyof ETagMappingAnalyticsEventProperties>
  | Pick<ExperimentTreatmentAnalyticsEventProperties, keyof ExperimentTreatmentAnalyticsEventProperties>
  | Pick<FavoriteModifiedAnalyticsEventProperties, keyof FavoriteModifiedAnalyticsEventProperties>
  | Pick<EmailSubscriptionAnalyticsEventProperties, keyof EmailSubscriptionAnalyticsEventProperties>
  | Pick<FeedAnalyticsEventProperties, keyof FeedAnalyticsEventProperties>
  | Pick<FeedbackAnalyticsEventProperties, keyof FeedbackAnalyticsEventProperties>
  | Pick<FeedCacheHitAnalyticsEventProperties, keyof FeedCacheHitAnalyticsEventProperties>
  | Pick<FeedFilterActionAnalyticsEventProperties, keyof FeedFilterActionAnalyticsEventProperties>
  | Pick<FirstRunExperienceAnalyticsEventProperties, keyof FirstRunExperienceAnalyticsEventProperties>
  | Pick<GifPostedEventProperties, keyof GifPostedEventProperties>
  | Pick<GroupAnalyticsEventProperties, keyof GroupAnalyticsEventProperties>
  | Pick<GroupClickedAnalyticsEventProperties, keyof GroupClickedAnalyticsEventProperties>
  | Pick<GroupSuggestionHoveredAnalyticsEventProperties, keyof GroupSuggestionHoveredAnalyticsEventProperties>
  | Pick<GroupSuggestionRejectedAnalyticsEventProperties, keyof GroupSuggestionRejectedAnalyticsEventProperties>
  | Pick<HashTagAnalyticsEventProperties, keyof HashTagAnalyticsEventProperties>
  | Pick<ImageLoadedAnalyticsEventProperties, keyof ImageLoadedAnalyticsEventProperties>
  | Pick<InboxNavigationAnalyticsEventProperties, keyof InboxNavigationAnalyticsEventProperties>
  | Pick<
      CampaignsDashboardNavigationAnalyticsEventProperties,
      keyof CampaignsDashboardNavigationAnalyticsEventProperties
    >
  | Pick<
      CampaignsDashboardCampaignNavigationAnalyticsEventProperties,
      keyof CampaignsDashboardCampaignNavigationAnalyticsEventProperties
    >
  | Pick<InclusiveReactionUseAnalyticsEventProperties, keyof InclusiveReactionUseAnalyticsEventProperties>
  | Pick<LeaderProfileLinkActionAnalyticEventProperties, keyof LeaderProfileLinkActionAnalyticEventProperties>
  | Pick<LeadershipCornerLinkActionAnalyticEventProperties, keyof LeadershipCornerLinkActionAnalyticEventProperties>
  | Pick<LeaderSuggestionAnalyticsEventProperties, keyof LeaderSuggestionAnalyticsEventProperties>
  | Pick<LegacyExperimentTreatmentAnalyticsEventProperties, keyof LegacyExperimentTreatmentAnalyticsEventProperties>
  | Pick<LinkPreviewGeneratedAnalyticsEventProperties, keyof LinkPreviewGeneratedAnalyticsEventProperties>
  | Pick<LiveEventsEventProperties, keyof LiveEventsEventProperties>
  | Pick<LoadNewerPillClickedAnalyticsEventProperties, keyof LoadNewerPillClickedAnalyticsEventProperties>
  | Pick<MessageActionAnalyticsEventProperties, keyof MessageActionAnalyticsEventProperties>
  | Pick<MessageBodyExpandedEventProperties, keyof MessageBodyExpandedEventProperties>
  | Pick<MobileAppPromotionEventProperties, keyof MobileAppPromotionEventProperties>
  | Pick<MobileUpsellAnalyticsEventProperties, keyof MobileUpsellAnalyticsEventProperties>
  | Pick<MomentOpenedProperties, keyof MomentOpenedProperties>
  | Pick<MomentsFollowAnalyticsEventProperties, keyof MomentsFollowAnalyticsEventProperties>
  | Pick<NavigatedToMessageEventProperties, keyof NavigatedToMessageEventProperties>
  | Pick<NetworkNavigationAnalyticsEventProperties, keyof NetworkNavigationAnalyticsEventProperties>
  | Pick<NetworkSwitcherPanelAnalyticsEventProperties, keyof NetworkSwitcherPanelAnalyticsEventProperties>
  | Pick<NextGroupCTAClickedProperties, keyof NextGroupCTAClickedProperties>
  | Pick<NotificationItemAnalyticsEventProperties, keyof NotificationItemAnalyticsEventProperties>
  | Pick<NotificationPanelAnalyticsEventProperties, keyof NotificationPanelAnalyticsEventProperties>
  | Pick<NotificationPickerAnalyticsEventProperties, keyof NotificationPickerAnalyticsEventProperties>
  | Pick<OutlookSubscriptionControlsAnalyticsEventProperties, keyof OutlookSubscriptionControlsAnalyticsEventProperties>
  | Pick<PropertyCheckEventProperties, keyof PropertyCheckEventProperties>
  | Pick<PublisherCollapsedEventProperties, keyof PublisherCollapsedEventProperties>
  | Pick<PublisherOnPostEventProperties, keyof PublisherOnPostEventProperties>
  | Pick<PublisherOpenEventProperties, keyof PublisherOpenEventProperties>
  | Pick<PublisherCancelEventProperties, keyof PublisherCancelEventProperties>
  | Pick<PublisherAddTopicClickedEventProperties, keyof PublisherAddTopicClickedEventProperties>
  | Pick<PushNotificationsSettingsBrowserPermissionGranted, keyof PushNotificationsSettingsBrowserPermissionGranted>
  | Pick<PushNotificationsSettingsBrowserPermissionRequested, keyof PushNotificationsSettingsBrowserPermissionRequested>
  | Pick<PushNotificationsSettingsSaved, keyof PushNotificationsSettingsSaved>
  | Pick<PageLoadedFromLinkClickAnalyticsEventProperties, keyof PageLoadedFromLinkClickAnalyticsEventProperties>
  | Pick<QAListClickEventProperties, keyof QAListClickEventProperties>
  | Pick<ReachButtonClickedProperties, keyof ReachButtonClickedProperties>
  | Pick<RealtimeReactionEventProperties, keyof RealtimeReactionEventProperties>
  | Pick<ReactionClickedProperties, keyof ReactionClickedProperties>
  | Pick<ReactionSelectorOpenedProperties, keyof ReactionSelectorOpenedProperties>
  | Pick<RepliesLoadMoreClickedEventProperties, keyof RepliesLoadMoreClickedEventProperties>
  | Pick<ReportMessageAnalyticsEventProperties, keyof ReportMessageAnalyticsEventProperties>
  | Pick<RichFormattingAnalyticsEventProperties, keyof RichFormattingAnalyticsEventProperties>
  | Pick<SharePointAnalyticsEventProperties, keyof SharePointAnalyticsEventProperties>
  | Pick<StorylineAdminSettingToggled, keyof StorylineAdminSettingToggled>
  | Pick<StorylineFREPaginationEventProperties, keyof StorylineFREPaginationEventProperties>
  | Pick<StorylineHomeClickedEventProperties, keyof StorylineHomeClickedEventProperties>
  | Pick<StorylineNotificationDefaultsSavedEventProperties, keyof StorylineNotificationDefaultsSavedEventProperties>
  | Pick<StorylineCreationControlsSavedEventProperties, keyof StorylineCreationControlsSavedEventProperties>
  | Pick<SurveyAnalyticsEventProperties, keyof SurveyAnalyticsEventProperties>
  | Pick<TeamsBackButtonEventProperties, keyof TeamsBackButtonEventProperties>
  | Pick<VivaSuiteNavEventProperties, keyof VivaSuiteNavEventProperties>
  | Pick<TeamsLaunchEventProperties, keyof TeamsLaunchEventProperties>
  | Pick<TeamsDeepLinkAnalyticsEventProperties, keyof TeamsDeepLinkAnalyticsEventProperties>
  | Pick<TeamsMeetingDeepLinkAnalyticsEventProperties, keyof TeamsMeetingDeepLinkAnalyticsEventProperties>
  | Pick<TeamsMeetingEventProperties, keyof TeamsMeetingEventProperties>
  | Pick<TeamsSuggestToPinAnalyticsEventProperties, keyof TeamsSuggestToPinAnalyticsEventProperties>
  | Pick<ThreadActionAnalyticsEventProperties, keyof ThreadActionAnalyticsEventProperties>
  | Pick<ThreadAnalyticsEventProperties, keyof ThreadAnalyticsEventProperties>
  | Pick<ThreadBestReplyMarkedEventProperties, keyof ThreadBestReplyMarkedEventProperties>
  | Pick<ThreadFollowedEventProperties, keyof ThreadFollowedEventProperties>
  | Pick<ThreadLinkCopiedEventProperties, keyof ThreadLinkCopiedEventProperties>
  | Pick<ThreadMessageAnalyticsEventProperties, keyof ThreadMessageAnalyticsEventProperties>
  | Pick<ThreadSharesClickEventProperties, keyof ThreadSharesClickEventProperties>
  | Pick<ToolbarEventProperties, keyof ToolbarEventProperties>
  | Pick<TopicAddedToThreadEventProperties, keyof TopicAddedToThreadEventProperties>
  | Pick<TopicAddedToPublisherEventProperties, keyof TopicAddedToPublisherEventProperties>
  | Pick<TopicAnalyticsEventProperties, keyof TopicAnalyticsEventProperties>
  | Pick<TopicBrowseEventProperties, keyof TopicBrowseEventProperties>
  | Pick<TopicClickEventProperties, keyof TopicClickEventProperties>
  | Pick<TopicReviewClickEventProperties, keyof TopicReviewClickEventProperties>
  | Pick<TopicReviewFeatureCompletedEventProperties, keyof TopicReviewFeatureCompletedEventProperties>
  | Pick<TopicManagementLinkClickedProperties, keyof TopicManagementLinkClickedProperties>
  | Pick<TrendingTopicActionAnalyticsEventProperties, keyof TrendingTopicActionAnalyticsEventProperties>
  | Pick<TypeaheadConversationAnalyticsEventProperties, keyof TypeaheadConversationAnalyticsEventProperties>
  | Pick<TypingPublishedAnalyticsEventProperties, keyof TypingPublishedAnalyticsEventProperties>
  | Pick<UserOptedinAnalyticsEventProperties, keyof UserOptedinAnalyticsEventProperties>
  | Pick<UserCoverPhotoUpdateEventProperties, keyof UserCoverPhotoUpdateEventProperties>
  | Pick<UserProfileClickedEventProperties, keyof UserProfileClickedEventProperties>
  | Pick<UserSuggestionAnalyticsEventProperties, keyof UserSuggestionAnalyticsEventProperties>
  | Pick<LeaderSuggestionAnalyticsEventProperties, keyof LeaderSuggestionAnalyticsEventProperties>
  | Pick<ViewAllCommunitiesAnalyticsEventProperties, keyof ViewAllCommunitiesAnalyticsEventProperties>
  | Pick<VivaTopicsFREClickedAnalyticsEventProperties, keyof VivaTopicsFREClickedAnalyticsEventProperties>
  | Pick<VisitedFromAnalyticsEventProperties, keyof VisitedFromAnalyticsEventProperties>
  | Pick<WebCardClickEventProperties, keyof WebCardClickEventProperties>
  | Pick<ReportBootstrapCompletedEventProperties, keyof ReportBootstrapCompletedEventProperties>
  | Pick<SearchTabClickedEventProperties, keyof SearchTabClickedEventProperties>
  | Pick<MomentsCollapsibleEventProperties, keyof MomentsCollapsibleEventProperties>
  | Pick<VivaAnswersTopicPickerEventProperties, keyof VivaAnswersTopicPickerEventProperties>
  | Pick<VivaAnswersOfficeTopicsEnabledProperties, keyof VivaAnswersOfficeTopicsEnabledProperties>;

export interface AnswersLinkActionAnalyticEventProperties {
  readonly from: 'left_nav';
}

export interface AskMeAnythingEventCreatedEventProperties {
  readonly teamsMeetingId: string;
  readonly descriptionIncluded: boolean;
  readonly startAt: string;
  readonly endAt: string;
  readonly organizerUserIds: string[];
  readonly moderatorUserIds: string[];
  readonly inviteeIds: string | string[];
  readonly isAnonymousPostingEnabled: boolean;
  readonly isMeetingPrivateToInvitees: boolean;
  readonly isNewQuestionNotificationEnabled: boolean;
  readonly isCalendarInviteToOrganizingTeamEnabled: boolean;
  readonly isReplyToConversationEnabled: boolean;
  readonly isThreadStarterAdminModerated: boolean;
  readonly isThreadStarterAdminOnly: boolean;
  readonly isThreadUpvoteEnabled: boolean;
}

export interface AskMeAnythingCreateClickedEventProperties {
  readonly source: AskMeAnythingEventCreationSource;
  readonly location: AskMeAnythingEventCreateButtonLocation;
  readonly card_type?: AskMeAnythingEventCardType;
}

export interface AskMeAnythingEventClickedEventProperties {
  readonly teamsMeetingId: string;
  readonly location: AskMeAnythingEventCardLocation;
}

export interface AskMeAnythingEventCardDisplayedProperties {
  readonly type: AskMeAnythingEventCardType;
  readonly location: AskMeAnythingEventCardLocation;
}

export interface AskMeAnythingEventListClickedEventProperties {
  readonly from: AskMeAnythingEventListClickedActionSource;
}

export interface CampaignsHubEntryPointClickedEventProperties {
  readonly from: string;
}

export interface CampaignsHubCardClickedEventProperties {
  readonly campaignId: string;
}

export interface ReportBootstrapCompletedEventProperties {
  readonly isDarkTheme: boolean | undefined;
}

export interface AutoRefreshFeedAnalyticsEventProperties {
  readonly feedType: string;
  readonly feedId?: string;
}

export interface ButtonClickedAnalyticsEventProperties {
  readonly groupNodeId: string;
  readonly groupId: string;
  readonly from?: JoinLeaveActionSource;
}

export interface DiscoveryGroupsSortAnalyticsEventProperties {
  readonly sortType: string;
}

export interface TeamsMeetingEventProperties {
  readonly officeMeetingId?: string;
  readonly teamsMeetingId?: string;
  readonly tenantId?: string | undefined;
  readonly isThreadStarterAdminModerated?: boolean;
  readonly isReplyToConversationEnabled?: boolean;
  readonly isThreadStarterAdminOnly?: boolean;
  readonly isThreadUpvoteEnabled?: boolean;
  readonly isAnonymousPostingEnabled?: boolean;
}

export interface TeamsBackButtonEventProperties {
  readonly backFrom: string;
}
export interface VivaSuiteNavEventProperties {
  readonly appName: string;
}

export interface EmailOpenAnalyticsEventProperties {
  readonly threadIds: string[];
}

export interface EmbedFeedCodeRetrievedEventProperties {
  readonly retrievedFrom: EmbedFeedCodeRetrievedFrom;
  readonly darkMode: boolean;
}

export enum EmbedFeedCodeRetrievedFrom {
  ConfigurationSite = 'configuration_site',
}

export interface ExternalLinkClickedEventProperties {
  readonly isEngageLink: boolean;
}

export interface ETagMappingAnalyticsEventProperties {
  readonly ETag: string;
  readonly ConfigIDs: string;
}

export interface ExperimentTreatmentAnalyticsEventProperties {
  readonly ProjectTeam: string;
  readonly TreatmentKey: string;
  readonly TreatmentValue: string | boolean | number;
  readonly ETag: string | undefined;
}

export interface FavoriteModifiedAnalyticsEventProperties {
  readonly action: FavoriteModifiedActionType;
  readonly entryPoint: FavoriteModifiedEntryPointType;
  readonly groupId: string;
}

export interface EmailSubscriptionAnalyticsEventProperties {
  readonly action: EmailSubscriptionActionType;
  readonly from: VisitedFromActionSource;
  readonly groupId: string | undefined;
}

export enum LoadSkippedReason {
  FeedNotLoaded = 'feedNotLoaded',
  NoOlderThreads = 'noOlderThreads',
  OlderThreadsLoading = 'olderThreadsLoading',
  LoadedEnoughOlderThreads = 'loadedEnoughOlderThreads',
}

export interface FeedAnalyticsEventProperties {
  readonly feedId: string;
  readonly threadCount: number;
  readonly reason?: LoadSkippedReason;
}

export interface FeedbackAnalyticsEventProperties {
  readonly type: 'proactive' | 'reactive';
}

export interface FeedCacheHitAnalyticsEventProperties {
  readonly feedType: string;
  readonly feedId?: string;
}

export interface FeedFilterActionAnalyticsEventProperties {
  readonly location: 'events' | 'groups' | 'topics' | 'users';
  readonly entryPoint: 'filter' | 'end_of_group' | 'sort';
  readonly currentFilter?:
    | BroadcastFeedType
    | GroupFeedType
    | GroupFeedThreadSortOrder
    | TopicFeedType
    | TopicNetworkQuestionFeedType
    | TopicUnifiedFeedType
    | UserFeedThreadSortOrder
    | UserWallFeedThreadSortOrder;
  readonly selectedFilter?:
    | BroadcastFeedType
    | GroupFeedType
    | GroupFeedThreadSortOrder
    | TopicFeedType
    | TopicNetworkQuestionFeedType
    | TopicUnifiedFeedType
    | UserFeedThreadSortOrder
    | UserWallFeedThreadSortOrder;
  readonly groupId?: string;
  readonly userId?: string;
}

export interface FirstRunExperienceAnalyticsEventProperties {
  readonly location: string;
}

export interface GifPostedEventProperties {
  readonly id: string;
  readonly type: string;
}

export interface GroupAnalyticsEventProperties {
  readonly groupId: string | undefined;
  readonly type: GroupAnalyticsEventType;
  readonly isAnswersCommunity?: boolean;
}

export interface CampaignAnalyticsEventProperties {
  readonly campaignId: string | undefined;
  readonly type: CampaignAnalyticsEventType;
}

export interface GroupSuggestionHoveredAnalyticsEventProperties {
  readonly from: string;
  readonly index: number;
  readonly recommendationId?: string;
}

export interface VisitedFromAnalyticsEventProperties {
  readonly from: VisitedFromActionSource;
}

export interface GroupClickedAnalyticsEventProperties {
  readonly groupId: string;
  readonly from: SuggestedRecommendedActionSource | VisitedFromActionSource | undefined;
  readonly index: number;
  readonly recommendationId: string | undefined;
}

export interface GroupSuggestionRejectedAnalyticsEventProperties {
  readonly groupId: string;
  readonly from: SuggestedRecommendedActionSource;
  readonly index: number;
  readonly recommendationId?: string;
}

export interface HashTagAnalyticsEventProperties {
  readonly hashTagId: string;
}

export interface ImageLoadedAnalyticsEventProperties {
  readonly imageOrigin: string;
  readonly imageType: string;
  readonly imageId?: string;
}

export interface InboxNavigationAnalyticsEventProperties {
  readonly selectedInboxFeedType: InboxFeedType;
  readonly unreadMessageCount: string | undefined;
}

export interface CampaignsDashboardNavigationAnalyticsEventProperties {
  readonly selectedCampaignsDashboardType: string;
}

export interface CampaignsDashboardCampaignNavigationAnalyticsEventProperties {
  readonly selectedCampaignHashtag: string;
}

export interface InclusiveReactionUseAnalyticsEventProperties {
  readonly reaction?: Reaction;
}

export interface LinkPreviewGeneratedAnalyticsEventProperties {
  readonly linkSource: LinkSource;
  readonly linkDescription: boolean;
  readonly linkName: boolean;
  readonly linkImage: boolean;
}

export interface LeaderProfileLinkActionAnalyticEventProperties {
  readonly from: 'highlight_carousel' | 'your_leaders_module' | 'leader_face_pile';
}

export interface LeadershipCornerLinkActionAnalyticEventProperties {
  readonly from: 'storyline' | 'home_feed' | 'quick_nav_collapsed' | 'quick_nav_open' | 'engage_digest';
}

export interface LegacyExperimentTreatmentAnalyticsEventProperties {
  readonly experiment: string;
  readonly treatmentGroup: 'control' | 'enabled';
}

export interface LiveEventsEventProperties {
  readonly broadcastId?: string;
  readonly eventType?: string;
  readonly groupId?: string;
  readonly isEmbeddable?: boolean;
  readonly status?: string;
  readonly location?: EventCardLocation;
  readonly selectedFilter?: string;
  readonly entryPoint?: string;
  readonly showOptimizedLayout?: boolean;
}

export interface LoadNewerPillClickedAnalyticsEventProperties {
  readonly broadcastId?: string;
  readonly feedType?: string;
  readonly messageCount: number;
  readonly threadId?: string;
  readonly threadLevel?: ThreadMessageLevel;
}

export interface MessageActionAnalyticsEventProperties {
  readonly messageId: string;
  readonly reaction?: Reaction;
  readonly feedType: string;
  readonly threadLevel?: ThreadMessageLevel;
}

export interface MessageBodyExpandedEventProperties {
  readonly feedType: string;
  readonly isThreadPinned: boolean;
  readonly isThreadStarter: boolean;
  readonly groupTelemetryId?: string;
  readonly messageId?: string;
}

export interface MobileAppPromotionEventProperties {
  readonly type: MobileAppPromotionType;
  readonly from: MobileAppPromotionSource;
}

export interface MobileUpsellAnalyticsEventProperties {
  readonly OsName: string;
  readonly browserType: string;
  readonly mobileUpsellType: MobileUpsellType;
  readonly mobileUpsellClient: MobileUpsellClient;
  readonly mobileUpsellTarget: string;
}

export interface MomentsFollowAnalyticsEventProperties {
  readonly from: string;
  readonly currentUserId: string;
}

export interface NetworkNavigationAnalyticsEventProperties {
  readonly isPrimary: boolean;
  readonly isLegacyExternalNetwork: boolean;
  readonly isGuestGroupAccessEnabled: boolean;
  readonly isMembershipPending: boolean;
  readonly clickedNetworkId: string;
}

export interface NetworkSwitcherPanelAnalyticsEventProperties {
  readonly networkCount: number;
}

export interface NotificationItemAnalyticsEventProperties {
  readonly notificationId: string;
  readonly notificationType: string;
  readonly isUnseen: boolean;
  readonly messageId?: string;
  readonly threadId?: string;
  readonly groupId?: string;
  readonly broadcastId?: string;
}

export interface NotificationFollowedByUserEventProperties extends NotificationItemAnalyticsEventProperties {
  readonly count: number;
}

export interface NotificationTeamsMeetingStartingProperties extends NotificationItemAnalyticsEventProperties {
  readonly teamsMeetingId: string;
}

export interface NotificationPanelAnalyticsEventProperties {
  readonly unseenCount: number;
}

export interface NotificationPickerAnalyticsEventProperties {
  readonly storylineUserId: string;
}

export interface OutlookSubscriptionControlsAnalyticsEventProperties {
  readonly action: EmailSubscriptionActionType;
}

export interface PushNotificationsSettingsBrowserPermissionGranted {
  readonly source: string;
}

export interface PushNotificationsSettingsBrowserPermissionRequested {
  readonly source: string;
}

export interface PushNotificationsSettingsSaved {
  readonly source: string;
  readonly value: boolean;
}

export interface ReachButtonClickedProperties {
  readonly publisherId: string;
  readonly publisherType: string;
  readonly groupId?: string;
}

export interface ReactionSelectorOpenedProperties {
  readonly feedType: TelemetryFeedType | undefined;
  readonly pickerType: 'Picker' | 'Dropdown';
  readonly messageId: string;
}

export interface ReportMessageAnalyticsEventProperties {
  readonly clientId: string;
  readonly groupId?: string;
  readonly isComment: boolean;
  readonly messageCount: number;
  readonly messageId: string;
  readonly networkId: string;
  readonly reportedUserId: string;
  readonly reportingUserId: string;
  readonly teamsMeetingId?: string;
  readonly threadId: string;
}

export interface SharePointAnalyticsEventProperties {
  readonly contentColumnWidth?: number;
  readonly feedLayoutType?: string;
  readonly feedSubtype?: string;
  readonly instanceId: string;
  readonly isDarkTheme?: boolean;
  readonly isMobileDevice?: boolean;
  readonly pageId: number;
  readonly showPublisher?: boolean;
  readonly siteId: string;
  readonly teamSiteGroupId?: string;
  readonly templateType: string;
}

export interface SurveyAnalyticsEventProperties {
  readonly campaignIds: string[];
  readonly clientSessionId: string;
}

export interface TeamsDeepLinkAnalyticsEventProperties {
  readonly entityId?: string;
  readonly entityType: string;
  readonly deepLinkId?: string;
  readonly deepLinkSubType?: string;
  readonly deepLinkType: string;
}

export interface TeamsLaunchEventProperties {
  readonly initiator: string;
}

export interface TeamsMeetingDeepLinkAnalyticsEventProperties extends TeamsDeepLinkAnalyticsEventProperties {
  readonly teamsMeetingId?: string;
}

export interface TeamsSuggestToPinAnalyticsEventProperties {
  readonly isDeepLinkNotification: boolean;
}

export interface ThreadActionAnalyticsEventProperties {
  readonly threadId: string;
  readonly threadStarterId: string;
}

export interface ThreadAnalyticsEventProperties {
  readonly threadId: string;
  readonly feedType?: TelemetryFeedType;
}

export interface ThreadBestReplyMarkedEventProperties {
  readonly threadId: string;
  readonly messageId: string;
  readonly isAnswersCommunity?: boolean;
  readonly groupId?: string;
}

export enum CopyLinkSource {
  ShareButton = 'share_button',
  ContextMenu = 'context_menu',
}

export interface ThreadLinkCopiedEventProperties {
  readonly threadId: string;
  readonly feedType?: TelemetryFeedType;
  readonly source: CopyLinkSource;
  readonly resourceId?: string;
}

export interface ThreadMessageAnalyticsEventProperties {
  readonly groupId?: string;
  readonly feedType: string;
  readonly isPinned: boolean;
  readonly isThreadStarter: boolean;
  readonly threadLevel?: ThreadMessageLevel;
  readonly threadId?: string;
  readonly messageCount?: number;
  readonly realtimeMessageCount?: number;
}

export interface TopicAddedToThreadEventProperties {
  readonly topicId: string;
  readonly threadId: string;
  readonly isAnswersCommunity: boolean;
  readonly feedType?: string;
  readonly from?: VisitedFromActionSource;
}

export interface TopicAddedToPublisherEventProperties {
  readonly topicId: string;
  readonly feedType: string | undefined;
  readonly conversationId: string | undefined;
  readonly isAnswersCommunity: boolean;
  readonly isUnifiedTopicPicker: boolean;
  readonly recommendationId?: string;
  readonly groupId?: string;
}

export interface TopicAnalyticsEventProperties {
  readonly topicId: string;
  readonly recommendationId?: string;
}

export interface ThreadFollowedEventProperties {
  readonly threadId: string;
  readonly isAnswersCommunity?: boolean;
  readonly groupId?: string;
}

export interface TopicBrowseEventProperties {
  readonly feedType?: TelemetryFeedType;
  readonly from?: TelemetryFeedSubtype | TopicBrowseActionSource;
  readonly to?: TopicBrowseActionSource;
  readonly topicId?: string;
}

export interface TrendingTopicActionAnalyticsEventProperties {
  readonly topicId: string;
  readonly topicPosition: number;
}

export interface CuratedTopicAnalyticsEventProperties extends TopicAnalyticsEventProperties {
  readonly recommendationId?: string;
}

export interface TypingPublishedAnalyticsEventProperties {
  readonly telemetryId: string;
  readonly feedType: TelemetryFeedType | undefined;
}

export interface TypeaheadConversationAnalyticsEventProperties {
  readonly conversationId?: string;
  readonly logicalId?: string;
  readonly index?: number;
  readonly suggestionType?: string;
  readonly typeaheadType?: string;
  readonly suggestionId?: string;
  readonly queryLength?: number;
  readonly wordCount?: number;
  readonly timeToActionMilliseconds?: number | undefined;
}

export interface UserOptedinAnalyticsEventProperties {
  readonly origin: OptinOrigin;
}

export interface UserProfileClickedEventProperties {
  readonly userId: string;
  readonly from: VisitedFromActionSource | undefined;
}

export interface UserSuggestionAnalyticsEventProperties {
  readonly from: SuggestedRecommendedActionSource;
  readonly recommendationId?: string;
  readonly suggestedUserId: string;
  readonly is_follow_back?: boolean;
}

export interface LeaderSuggestionAnalyticsEventProperties {
  readonly from: SuggestedRecommendedActionSource;
  readonly recommendationId?: string;
  readonly suggestedLeaderId: string;
}

export interface PageLoadedFromLinkClickAnalyticsEventProperties {
  readonly scenarioType?: string;
  readonly integrationOrigin?: string;
  readonly scenarioSubType?: string;
  readonly subEvent?: string;
  readonly emailId?: string;
  readonly threadId?: string;
  readonly copyLink?: string;
  readonly resourceId?: string;
}

export interface ViewAllCommunitiesAnalyticsEventProperties {
  readonly communitiesCount: number;
  readonly favoritesCount?: number;
}

export interface VivaTopicsFREClickedAnalyticsEventProperties {
  readonly type: 'banner' | 'next' | 'previous' | 'confirmed' | 'dismissed';
}

export interface VivaAnswersTopicPickerEventProperties {
  readonly feedType: string | undefined;
}

export interface VivaAnswersOfficeTopicsEnabledProperties {
  readonly isOfficeTopicsExperimentEnabled: boolean;
  readonly isOfficeTopicsEnabledForNetwork: boolean;
}

export interface WebCardClickEventProperties {
  readonly linkType: LinkSource;
  readonly feedLocation: FeedLocation;
}

export interface ThreadSharesClickEventProperties {
  readonly threadId: string;
  readonly userId: string;
}

export interface RepliesLoadMoreClickedEventProperties {
  readonly threadId: string;
  readonly feedType: string;
  readonly direction: ReplyUnrollDirection;
  readonly groupId?: string;
  readonly threadLevel?: ThreadMessageLevel;
}

export interface UserCoverPhotoUpdateEventProperties {
  readonly userId: string;
}

export enum ReplyUnrollDirection {
  Next = 'next',
  Previous = 'previous',
}

export type OptinOrigin = 'forced' | 'optin_dialog' | 'toggle';

export enum JoinLeaveActionSource {
  DiscoveryPageAllCard = 'discovery_page_all_group_card',
  DiscoveryPageSuggestedCard = 'discovery_page_suggested_group_card',
  FirstRunExperience = 'first_run_groups',
  GroupHeader = 'group_header',
  GroupHeaderCompact = 'group_header_compact',
  GroupCampaignSidebarWidget = 'group_campaign_sidebar_widget',
  InaccessibleGroup = 'sharepoint_inaccessible_group',
  JoinGroupsLightbox = 'join_groups_lightbox',
  RelatedGroup = 'related_group_card',
  SuggestedGroup = 'rr_suggested_group_card',
  SuggestedUser = 'rr_suggested_people_card',
  UserProfileGroup = 'user_profile_group',
}

export enum AskMeAnythingEventListClickedActionSource {
  UserStoryline = 'user_storyline',
  LeadershipCorner = 'leadership_corner',
}

export enum SuggestedRecommendedActionSource {
  DiscoveryPageAllCard = 'discovery_page_all_group_card',
  DiscoveryPageRecommendedCard = 'discovery_page_suggested_group_card',
  StorylineHomePage = 'storyline_home_page',
  SuggestedGroup = 'rr_suggested_group_card',
  SuggestedUser = 'rr_suggested_people_card',
  Home = 'home_feed',
  UserCarousel = 'user_carousel',
  FollowUsersLightbox = 'follow_users_lightbox',
  SuggestedLeader = 'suggested_leader_module',
}

export enum VisitedFromActionSource {
  AlgorithmGroupLabel = 'algorithm_group_label',
  AlgorithmStorylineLabel = 'algorithm_storyline_label',
  AllLeaders = 'all_leaders',
  AMAEventPage = 'ama_event_page',
  AnnouncementLabel = 'announcement_label',
  AnswersFAQify = 'answers_faqify',
  AnswersFAQifyListView = 'answers_faqify_list_view',
  AnswersSingleQuestion = 'answers_single_question',
  BroadcastEventDescription = 'broadcast_event_description',
  CommunityCampaign = 'community_campaign',
  ConversationCard = 'conversation_card',
  DiscoverCommunities = 'discover_communities_page_community_card',
  DiscoverCommunitiesAllTab = 'discover_communities_all_tab',
  DiscoveryNarrowEventCard = 'discovery_narrow_event_card',
  DiscoveryPageAllCard = 'discovery_page_all_group_card',
  DiscoveryPageRecommendedCard = 'discovery_page_suggested_group_card',
  FollowersModal = 'followers_modal',
  FollowingCampaignGroupLabel = 'following_campaign_group_label',
  FollowingCampaignStorylineLabel = 'following_campaign_storyline_label',
  FollowingSendersGroupLabel = 'following_senders_group_label',
  FollowingSendersStorylineLabel = 'following_senders_storylinelabel',
  FollowingThreadStarterSenderGroupLabel = 'following_thread_starter_sender_group_label',
  FollowingThreadStarterSenderStorylineLabel = 'following_thread_starter_sender_storyline_label',
  FollowingTopicGroupLabel = 'following_topic_group_label',
  FollowingTopicStorylineLabel = 'following_topic_storyline_label',
  GroupCoverPhoto = 'group_cover_photo',
  GroupDescription = 'group_description',
  GroupFeed = 'group_feed',
  GroupHeaderAvatar = 'group_header_avatar',
  GroupHeaderName = 'group_header_name',
  GroupNameBannerHeader = 'group_name_banner_header',
  GroupToGroupNavigation = 'group_to_group_navigation',
  HomeSidebarMyGroup = 'home_sidebar_my_group',
  InaccessibleGroup = 'embed_inaccessible_group',
  InboxFeedFooter = 'inbox_feed_footer',
  InboxThreadFooter = 'inbox_thread_footer',
  LeftNav = 'left_nav',
  LiveEventCard = 'live_event_card',
  LiveEventHeader = 'live_event_header',
  MessageBody = 'message_body',
  MomentsCreation = 'moments_creation',
  MomentsModal = 'moments_modal',
  MoveThreadDialog = 'move_thread_dialog',
  MyGroupsCard = 'my_groups_page_group_card',
  NavigationCommunityInsights = 'navigation_community_insights',
  NotificationFollowBack = 'notification_follow_back',
  NotificationFollowBackLightbox = 'notification_follow_back_lightbox',
  NotificationItem = 'notification_item',
  PostedInGroupLabel = 'posted_in_group_label',
  PromotedLabel = 'promoted_label',
  PromotedStorylineLabel = 'promoted_storyline_label',
  PublisherRecordVideo = 'publisher_record_video',
  PublisherAttachMedia = 'publisher_attach_media',
  QuickNavPanel = 'quick_nav_panel',
  QuickNavPanelCollapsed = 'quick_nav_panel_collapsed',
  RecommendationReasonLabel = 'recommendation_reason_label',
  RecommendedCampaignGroupLabel = 'recommended_campaign_group_label',
  RecommendedCampaignStorylineLabel = 'recommended_campaign_storyline_label',
  RecommendedGroupLabel = 'recommended_group_label',
  RecommendedLeaderLabel = 'recommended_leader_label',
  RecommendedTopicLabel = 'recommended_topic_label',
  RecommendedUserStorylineLabel = 'recommended_user_storyline_label',
  RecommendedUserGroupLabel = 'recommended_user_group_label',
  RelatedCommunity = 'related_community',
  SearchResultFile = 'search_result_file',
  SearchResultGroup = 'search_result_group',
  SearchResultCampaign = 'search_result_campaign',
  SearchResultGroupAvatar = 'search_result_group_avatar',
  SharedMessage = 'shared_message',
  StorylineAnnouncementLabel = 'storyline_announcement_label',
  SubHeaderCommunityInsights = 'sub_header_community_insights',
  SuggestedCommunity = 'suggested_community',
  SuggestedLeaderModule = 'suggested_leader_module',
  SystemMessageGroupCreated = 'system_message_group_created',
  SystemMessageMoveDirectMessageToGroup = 'system_message_move_direct_message_to_group',
  SystemMessageMoveMessageFromGroup = 'system_message_move_message_from_group',
  SystemMessageMoveMessageToGroup = 'system_message_move_message_to_group',
  TeamsGroupHeader = 'teams_group_header',
  ThreadCardAnnouncement = 'thread_card_announcement',
  ThreadCardLabelGroup = 'thread_card_label_group',
  ThreadCardLabelUserStoryline = 'thread_card_label_user_storyline',
  ThreadCardLabelUserStorylineAnnouncement = 'thread_card_label_user_storyline_announcement',
  ThreadSharesHeader = 'thread_shares_header',
  TrendingGroupLabel = 'trending_group_label',
  TrendingStorylineLabel = 'trending_storyline_label',
  UserProfileGroup = 'user_profile_group',
  UserProfileHeader = 'user_profile_header',
  UserSidebarGroupAvatar = 'user_sidebar_group_avatar',
  UserSidebarGroupName = 'user_sidebar_group_name',
  VivaEngageNavigationHeader = 'viva_engage_navigation_header',
}

export enum TopicBrowseActionSource {
  Trending = 'answers_topics_browse_trending',
  Following = 'answers_topics_browse_following',
  Recommended = 'answers_topics_browse_recommended',
  Featured = 'answers_topics_browse_featured',
  All = 'answers_topics_browse_all',
  Proposed = 'answers_topics_browse_needs_review',
  Discovery = 'answers_discover_new_topics',
  Search = 'answers_topics_browse_search',
  Suggested = 'answers_suggested_topics',
  EngageSuggested = 'engage_suggested_topics',
  OfficeTopic = 'office_topic_page',
  Topic = 'answers_topic_page',
  OfficeTopicFollowers = 'office_topic_followers',
  NetworkQuestionTopicFollowers = 'answers_topic_followers',
  TopicSuggest = 'answers_topic_suggest',
  TopicCreate = 'answers_topic_create',
  OfficeTopicCreate = 'office_topic_create',
  TopicEdit = 'answers_topic_edit',
  TopicFeature = 'answers_topic_feature',
  Onboarding = 'answers_onboarding',
  OnboardingSearch = 'answers_onboarding_search',
  UnifiedTopic = 'unified_topic_page',
}

export enum FavoriteModifiedEntryPointType {
  Header = 'header',
  CommunitiesList = 'communities_list',
}

export enum FavoriteModifiedActionType {
  Add = 'add',
  Remove = 'remove',
}

export enum EmailSubscriptionActionType {
  Subscribe = 'subscribe',
  Unsubscribe = 'unsubscribe',
}

export enum LinkSource {
  SharePointPageLink = 'SP_page_link',
  SharePointDocumentLink = 'SP_document_link',
  SharePointSiteLink = 'SP_site_link',
  SharePointLink = 'SP_link',
  WebLink = 'web_link',
}

export enum CampaignAnalyticsEventType {
  OpenPinnedResource = 'open_pinned_resource',
  CampaignPinnedResourcesActivated = 'campaign_pinned_resources_activated',
  CampaignCoverImageModified = 'campaign_cover_image_modified',
}

export enum GroupAnalyticsEventType {
  AddUser = 'add_user',
  AddUserByEmail = 'add_user_by_email',
  EmbedFeedCodeRetrieved = 'embed_feed_code_retrieved',
  GroupSettings = 'group_settings',
  MakeAdmin = 'make_admin',
  OpenPinnedResource = 'open_pinned_resource',
  PostByEmailRetrieved = 'post_by_email_retrieved',
  RemoveUser = 'remove_user',
  RevokeAdmin = 'revoke_admin',
  SubscribeByEmail = 'subscribe_by_email',
  UnsubscribeByEmail = 'unsubscribe_by_email',
  HideGroupForViewerInDiscovery = 'hide_group_for_viewer_in_discovery',
  UnhideGroupForViewerInDiscovery = 'unhide_group_for_viewer_in_discovery',
  HideGroupForNetworkInDiscovery = 'hide_group_for_network_in_discovery',
  UnhideGroupForNetworkInDiscovery = 'unhide_group_for_network_in_discovery',
  MarkAsOfficial = 'mark_as_official',
  UnmarkAsOfficial = 'unmark_as_official',
  MarkGroupAsMultiTenantOrganizationGroup = 'mark_as_mto_group',
  UnmarkGroupAsMultiTenantOrganizationGroup = 'unmark_as_mto_group',
  ClearGroupUnseen = 'clear_group_unseen',
  EditFlowEditGroupClicked = 'edit_flow_edit_group_clicked',
  EditGroupLightboxDisplayed = 'edit_group_lightbox_displayed',
  EditGroupLightboxExited = 'edit_group_lightbox_exited',
  CopyLinkToGroupClicked = 'group_copy_link_to_clipboard_clicked',
}

export type MobileUpsellType =
  | 'MobileWebMiniUpsell'
  | 'MobileWebFullUpsell'
  | 'MobileWebLiveEventsUpsell'
  | 'MobileWebSignedOutUpsell'
  | 'DesktopWebRightRailUpsell';

export enum MobileUpsellTypeValues {
  MobileWebMiniUpsell = 'MobileWebMiniUpsell',
  MobileWebFullUpsell = 'MobileWebFullUpsell',
  MobileWebLiveEventsUpsell = 'MobileWebLiveEventsUpsell',
  MobileWebSignedOutUpsell = 'MobileWebSignedOutUpsell',
  DesktopWebRightRailUpsell = 'DesktopWebRightRailUpsell',
}

export enum MobileAppPromotionSource {
  ThreadPage = 'thread_page',
  HomePage = 'home_page',
}

export enum MobileAppPromotionType {
  QRCode = 'qr_code',
  Banner = 'banner',
  CoachingBar = 'coaching_bar',
}

export type MobileUpsellClient = 'GlammerMobile' | 'GlammerWeb';

export enum UnknownUserAgent {
  BrowserType = 'Unknown Browser Name',
  OsName = 'Unknown OS Name',
}

export interface ReactionClickedProperties {
  readonly source: 'ReactionPicker' | 'Button';
  readonly pickerType: 'Picker' | 'Dropdown' | 'CopyReaction';
  readonly feedType: TelemetryFeedType | undefined;
  readonly reaction: Reaction;
  readonly previousReaction: Reaction | undefined;
  readonly skintoneApplied: boolean;
  readonly groupId: string | undefined;
  readonly teamsMeetingId: string | undefined;
  readonly threadId: string;
  readonly messageId: string;
  readonly isAnnouncement: boolean;
  readonly isPromotedThread: boolean;
  readonly feedLocation: FeedLocation;
  readonly isAnswersCommunity: boolean;
  readonly from?: string;
}

export interface NavigatedToMessageEventProperties {
  readonly threadId: string;
  readonly topLevelMessageId?: string;
  readonly secondLevelMessageId?: string;
}

export enum EventCardLocation {
  Discovery = 'discovery',
  Group = 'group',
}

export enum AskMeAnythingEventCardLocation {
  HomeRightRail = 'home_right_rail',
  LeadersRightRail = 'leaders_right_rail',
  LeadersEventsList = 'leaders_events_list',
  UserRightRail = 'user_right_rail',
  UserEventsList = 'user_events_list',
  GroupRightRail = 'group_right_rail',
}

export enum AskMeAnythingEventCreateButtonLocation {
  HomeRightRail = 'home_right_rail',
  UserRightRail = 'user_right_rail',
  UserEventListHeader = 'user_event_list_header',
  UserNoEventsPage = 'user_no_events_page',
}

export enum AskMeAnythingEventCreationSource {
  CardTitleActionButton = 'card_title_action_button',
  CardFooterLink = 'card_footer_link',
  Button = 'button',
}

export enum AskMeAnythingEventCardType {
  CreationCard = 'creation_card',
  EmptyStateCard = 'empty_state_card',
  FeaturedEventsCard = 'featured_events_card',
}

export interface CreateCommunityClickedProperties {
  readonly from: 'discovery' | 'left_nav';
}

export interface CreationFlowCreateGroupSucceededProperties {
  readonly groupId: string;
}

export interface CreationFlowCreateCampaignSucceededProperties {
  readonly campaignId: string;
}

export interface CreateMomentClickedProperties {
  readonly type: MomentCreationType;
}

export interface EditFlowEditCampaignSucceededProperties {
  readonly campaignId: string;
  readonly actionType?: string;
}

export interface TopicManagementLinkClickedProperties {
  readonly topicId: string;
}

export interface TopicClickEventProperties {
  readonly groupId?: string;
  readonly topicId?: string;
  readonly threadId?: string;
  readonly feedType?: TelemetryFeedType;
  readonly from?: TelemetryFeedSubtype;
}

export interface TopicReviewClickEventProperties {
  readonly topicId: string;
  readonly from: VisitedFromActionSource;
}

export interface TopicReviewFeatureCompletedEventProperties extends TopicReviewClickEventProperties {
  readonly abstractEdit: boolean;
}

export interface QAListClickEventProperties {
  readonly messageId: string;
  readonly topicId?: string;
}

export interface MomentOpenedProperties {
  readonly feedType: string;
  readonly source: MomentOpenedSource;
  readonly authorId: string;
}

export interface CoachingTipProperties {
  readonly from: CoachingTipSource;
  readonly type: string;
  readonly suggestedUserIndex?: number;
  readonly suggestedUserRank?: number;
}

export interface CommunityInsightsProperties {
  readonly groupId: string;
  readonly viewType?: string;
  readonly timePeriod?: ActivityTimeframeType;
}

export interface CommunityBadgeDisplayedProperties {
  readonly badgeType: MessageSenderBadge;
  readonly messageId: string;
}

export interface ConversationViewInsightsProperties {
  readonly threadId: string | undefined;
  readonly groupId: string | undefined;
  readonly scopeType: ScopeType;
  readonly source?: ConversationInsightsOpenedSource;
}

export enum MomentOpenedSource {
  Previous = 'Previous',
  Next = 'Next',
  FeedThumbnail = 'FeedThumbnail',
  ModalThumbnail = 'ModalThumbnail',
}

export enum CoachingTipSource {
  UserProfile = 'UserProfile',
  Storylines = 'Storylines',
  HomeFeed = 'HomeFeed',
  SingleThread = 'SingleThread',
  LeadershipCorner = 'LeadershipCorner',
}

export enum ConversationInsightsOpenedSource {
  SeenBy = 'SeenBy',
  DropDown = 'Dropdown',
}

export enum ScopeType {
  YammerInfluencer = 'yammer_influencer',
  YammerGroup = 'yammer_group',
}

export interface NextGroupCTAClickedProperties {
  readonly groupId: string;
  readonly nextGroupId: string;
}

export interface RealtimeReactionEventProperties {
  readonly messageId: number;
  readonly reactionType: string;
}

export interface StorylineAdminSettingToggled {
  readonly isToggledOn: boolean;
}

export interface StorylineFREPaginationEventProperties {
  readonly page: number;
}

export interface StorylineHomeClickedEventProperties {
  readonly userId: string;
  readonly from: VisitedFromActionSource | undefined;
}

export interface StorylineNotificationDefaultsSavedEventProperties {
  readonly storylineNotificationChannels: string[];
}

export interface StorylineCreationControlsSavedEventProperties {
  readonly officeGroupMemberCount: number;
}

export interface SearchTabClickedEventProperties {
  readonly type: string;
  readonly conversationId?: string;
}

export enum MomentsCollapsibleEventTrigger {
  UserInitiated = 'UserInitiated',
  InitialLoad = 'InitialLoad',
}

export enum MomentsCollapsibleEventState {
  Collapsing = 'Collapsing',
  Expanding = 'Expanding',
  Hidden = 'Hidden',
  Failed = 'Failed',
}

export interface MomentsCollapsibleEventProperties {
  readonly trigger: MomentsCollapsibleEventTrigger;
  readonly state: MomentsCollapsibleEventState;
}

export enum AnalyticsEventName {
  AddThreadTopicsClicked = 'add_thread_topics_clicked',
  AddTopicScreenLoaded = 'add_topic_screen_loaded',
  AdminAnswersEnabledToggleClicked = 'admin_answers_enabled_toggle_clicked',
  AdminAnswersAIFeaturesToggleClicked = 'admin_answers_ai_features_toggle_clicked',
  AdminAnswersOnlyEnabledToggleClicked = 'admin_answers_only_enabled_toggle_clicked',
  AdminAnswersFeatureManagementClicked = 'admin_answers_feature_management_clicked',
  AMAAddToCalendarClicked = 'ama_add_to_calendar_clicked',
  AMACardDisplayed = 'ama_card_displayed',
  AMACalendarEventAdded = 'ama_calendar_event_added',
  AMAEventCreated = 'ama_event_created',
  AMACreateClicked = 'ama_create_clicked',
  AMAEventClicked = 'ama_event_clicked',
  AMAEventListClicked = 'ama_event_list_clicked',
  AMACopyLinkClicked = 'ama_copy_link_clicked',
  AMAShareToStorylineClicked = 'ama_share_to_storyline_clicked',
  AnnouncementTeachingModalDismissedClicked = 'announcement_teaching_modal_dismissed_clicked',
  AnnouncementTeachingModalTriggered = 'announcement_teaching_modal_triggered',
  AnnouncementTeachingModalTryItClicked = 'announcement_teaching_modal_try_it_clicked',
  AnswersLinkClicked = 'answers_link_clicked',
  AnswersTabClicked = 'answers_tab_clicked',
  AnswersBrowseAllTabClicked = 'answers_browse_topics_all_clicked',
  AnswersBrowseFeaturedTabClicked = 'answers_browse_topics_featured_clicked',
  AnswersBrowseFollowingTabClicked = 'answers_browse_topics_following_clicked',
  AnswersBrowseProposedTabClicked = 'answers_browse_topics_needs_review_clicked',
  AnswersBrowseRecommendedTabClicked = 'answers_browse_topics_recommended_clicked',
  AnswersBrowseTrendingTabClicked = 'answers_browse_topics_trending_clicked',
  AnswersTopicFeedSeeMore = 'topic_feed_see_more',
  AnswersTopicPageVisited = 'topic_page_visited',
  AnswersTopicSuggestedDismissed = 'suggested_topic_dismissed',
  AnswersTopicSuggestedRefreshed = 'suggested_topic_refreshed',
  AnswersBrowseTopicsClicked = 'browse_topics_clicked',
  AnswerUpvoteFreDismissed = 'answer_upvote_fre_dismissed',
  AnswerUpvoteFreRendered = 'answer_upvote_fre_rendered',
  AutoRefreshFeed = 'auto_refresh_feed',
  BookmarkClicked = 'bookmark_clicked',
  BookmarkTabClicked = 'bookmark_tab_clicked',
  BookmarksButtonClicked = 'bookmarks_button_clicked',
  BootstrapCompleted = 'bootstrap_completed',
  BrowseExternalNetworksClicked = 'browse_external_networks_clicked',
  CampaignClicked = 'campaign_clicked',
  CampaignsHubEntryPointClicked = 'campaigns_hub_entry_point_clicked',
  CampaignsHubCardClicked = 'campaigns_hub_card_clicked',
  CampaignsHubPageVisited = 'campaigns_hub_page_visited',
  CampaignDisplayed = 'campaign_displayed',
  CampaignDeletedErrorDisplayed = 'campaign_deleted_error_displayed',
  CampaignInAccessibleErrorDisplayed = 'campaign_inaccible_error_displayed',
  CampaignRejectedErrorDisplayed = 'campaign_rejected_error_displayed',
  CampaignSideBarCardDisplayed = 'campaign_sidebar_card_displayed',
  CarouselCardArrowClicked = 'carousel_card_arrow_clicked',
  CoachingTipDisplayed = 'coaching_tip_displayed',
  CoachingTipDismissed = 'coaching_tip_dismissed',
  CoachingTipTryItClicked = 'coaching_tip_try_it_clicked',
  CommunitiesTabClicked = 'communities_tab_clicked',
  CommunityBadgeDisplayed = 'community_badge_displayed',
  CommunityInsightsDownloadCSVClicked = 'comm_insights_download_csv_clicked',
  CommunityInsightsExportOptionsClicked = 'comm_insights_export_options_clicked',
  CommunityInsightsOverViewLoaded = 'comm_insights_overview_loaded',
  CommunityInsightsQAViewLoaded = 'comm_insights_qa_view_loaded',
  ConversationCardExpanded = 'conversation_card_expanded',
  ConversationTrendsClicked = 'conv_trends_clicked',
  ConversationViewInsightsClicked = 'conv_view_insights_clicked',
  CoverPhotoModified = 'cover_photo_modified',
  CreateCampaignClicked = 'create_campaign_clicked',
  CreateCampaignLightboxDisplayed = 'create_a_campaign_lightbox_displayed',
  CreateCampaignLightboxExited = 'create_a_campaign_lightbox_exited',
  CreateCommunityClicked = 'create_community_clicked',
  CreateGroupCalendarForBroadcastClicked = 'create_group_calendar_for_broadcast_clicked',
  CreateGroupLightboxDisplayed = 'create_a_group_lightbox_displayed',
  CreateGroupLightboxExited = 'create_a_group_lightbox_exited',
  CreateMomentClicked = 'create_moment_clicked',
  CreateMomentThumbnailClicked = 'create_moment_thumbnail_clicked',
  CreateNewTopicClicked = 'create_new_topic_clicked',
  CreateNewTopicScreenLoaded = 'create_new_topic_screen_loaded',
  CreateTeamsMeetingRequested = 'create_teams_meeting_requested',
  CreationFlowAdvancedOptionsExpanded = 'creation_flow_advanced_options_expanded',
  CreationFlowClassificationDropdownChanged = 'creation_flow_classification_dropdown_changed',
  CreationFlowCampaignNameTypingStarted = 'creation_flow_campaign_name_typing_started',
  CreationFlowCampaignHashtagTypingStarted = 'creation_flow_campaign_hashtag_typing_started',
  CreationFlowCampaignOrganizerPickerStarted = 'creation_flow_campaign_organizer_picker_started',
  CreationFlowCampaignDescriptionTypingStarted = 'creation_flow_campaign_description_typing_started',
  CreationFlowCampaignPublisherChanged = 'creation_flow_campaign_publisher_changed',
  CreationFlowCampaignShouldNotifyByEmailChanged = 'creation_flow_campaign_should_notify_by_email_changed',
  CreationFlowCampaignThemeColorChanged = 'creation_flow_campaign_themecolor_changed',
  CreationFlowCampaignCollaboratorTypingStarted = 'creation_flow_campaign_collaborator_typing_started',
  CreationFlowCampaignCollaboratorAdded = 'creation_flow_campaign_collaborator_added',
  CreationFlowCampaignCollaboratorRemoved = 'creation_flow_campaign_collaborator_removed',
  CreationFlowCreateCampaignClicked = 'campaign_create_button_clicked',
  CreationFlowCreateCampaignSucceeded = 'campaign_creation_succeeded',
  CreationFlowCreateGroupClicked = 'group_create_button_clicked',
  CreationFlowCreateGroupSucceeded = 'group_creation_succeeded',
  CreationFlowDescriptionTypingStarted = 'creation_flow_description_typing_started',
  CreationFlowGroupMemberAdded = 'creation_flow_group_member_added',
  CreationFlowGroupMemberTypingStarted = 'creation_flow_group_member_typing_started',
  CreationFlowGroupNameTypingStarted = 'creation_flow_group_name_typing_started',
  CreationFlowInternalExternalDropdownChanged = 'creation_flow_internal_external_dropdown_changed',
  CreationFlowPrivacyDropdownChanged = 'creation_flow_privacy_dropdown_changed',
  CreationFlowSensitivityDropdownChanged = 'creation_flow_sensitivity_dropdown_changed',
  CreationFlowUsageGuidelinesClicked = 'creation_flow_usage_guidelines_clicked',
  CuratedTopicCreated = 'curated_topic_created',
  CuratedTopicFeatured = 'curated_topic_featured',
  DarkModeTeachingBubbleDismissed = 'dark_mode_teaching_bubble_dismissed',
  DarkModeTeachingBubbleEnabledFeature = 'dark_mode_teaching_bubble_enabled_feature',
  DarkModeTeachingBubbleShown = 'dark_mode_teaching_bubble_shown',
  PendingPostTabClicked = 'pending_post_tab_clicked',
  DraftsButtonClicked = 'drafts_button_clicked',
  DraftTabClicked = 'draft_tab_clicked',
  ScheduledTabClicked = 'scheduled_tab_clicked',
  SchedulePostModalOpened = 'schedule_post_modal_opened',
  SchedulePostModalDismissed = 'schedule_post_modal_dismissed',
  SchedulePostButtonClicked = 'schedule_post_button_clicked',
  RescheduleButtonClicked = 'reschedule_button_clicked',
  DeprecationBannerDismissed = 'deprecation_banner_dismissed',
  DeprecationBannerHidden = 'deprecation_banner_hidden',
  DeprecationBannerLoaded = 'deprecation_banner_loaded',
  DesignerButtonClicked = 'designer_button_clicked',
  DesignerAppLoaded = 'designer_app_loaded',
  DiscoverCommunitiesClicked = 'discover_communities_clicked',
  DiscoverCommunitiesDefaultNavClicked = 'discover_communities_link_clicked',
  DiscoverGroupsTabAllClicked = 'discover_groups_tab_all_clicked',
  DiscoverLeadersLinkClicked = 'discover_leaders_link_clicked',
  DiscoveryEventCardGroupClicked = 'discovery_event_card_group_clicked',
  DiscoveryPageCreateCommunityClicked = 'discovery_page_create_community_clicked',
  DiscoveryPageSortClicked = 'discovery_page_sort_clicked',
  DownloadVideoButtonClicked = 'download_video_button_clicked',
  EcsEtagMapping = 'ecs_etag_mapping',
  EcsExperimentTreatment = 'ecs_experiment_treatment',
  EditAttachmentsFreDismissed = 'edit_attachments_fre_dismissed',
  EditAttachmentsFreRendered = 'edit_attachments_fre_rendered',
  EditFlowDeleteCampaignClicked = 'delete_campaign_clicked',
  EditFlowDeleteCampaignSucceeded = 'delete_campaign_succeeded',
  EditFlowDeleteCampaignFailed = 'delete_campaign_failed',
  EditFlowEditCampaignClicked = 'edit_campaign_clicked',
  EditFlowEditCampaignSucceeded = 'edit_campaign_succeeded',
  EditFlowEditCampaignLightboxDisplayed = 'edit_campaign_lightbox_displayed',
  EditFlowEditCampaignLightboxExited = 'edit_campaign_lightbox_exited',
  EditFlowEditCampaignSponsorsLightboxDisplayed = 'edit_campaign_sponsors_lightbox_displayed',
  EditFlowEditCampaignSponsorsLightboxExited = 'edit_campaign_sponsors_lightbox_exited',
  EditFlowEditCampaignOrganizersLightboxDisplayed = 'edit_campaign_organizers_lightbox_displayed',
  EditFlowEditCampaignOrganizersLightboxExited = 'edit_campaign_organizers_lightbox_exited',
  EditFlowPublishCampaignLightboxDisplayed = 'publish_campaign_lightbox_displayed',
  EditFlowPublishCampaignLightboxExited = 'publish_campaign_lightbox_exited',
  EditFlowDeleteCampaignLightboxDisplayed = 'delete_campaign_lightbox_displayed',
  EditFlowDeleteCampaignLightboxExited = 'delete_campaign_lightbox_exited',
  EditFlowCloseCampaignLightboxDisplayed = 'close_campaign_lightbox_displayed',
  EditFlowCloseCampaignLightboxExited = 'close_campaign_lightbox_exited',
  EditGifPickerClicked = 'edit_gif_picker_clicked',
  EditGroupLightbox = 'edit_group_lightbox',
  EditGoalTrackerQuantityTypingStarted = 'edit_goaltracker_quantity_typing_started',
  EditGoalTrackerUnitChanged = 'edit_goaltracker_quantity_typing_started',
  EditTeamsMeetingRequested = 'edit_teams_meeting_requested',
  EditThreadTopicsClicked = 'edit_thread_topics_clicked',
  EditUploadFileClicked = 'edit_upload_file_clicked',
  EmailExplanationClicked = 'email_explanation_clicked',
  EmailHeaderUserAvatarClicked = 'email_header_user_avatar_clicked',
  EmailOpen = 'email_open',
  EmailSubscriptionToggleClicked = 'email_subscription_toggle_clicked',
  EmailUnsubscribeConfirmed = 'email_unsubcribe_confirmed',
  EmailUnsubscribeDeclined = 'email_unsubcribe_declined',
  EmbedFeedCodeRetrieved = 'embed_feed_code_retrieved',
  EventCancelled = 'cancel_live_event_clicked',
  EventsAttendeeShareLinkClicked = 'events_attendee_share_link_clicked',
  EventsCardClicked = 'events_card_clicked',
  EventsCardShown = 'events_card_shown',
  EventsCreateEventClicked = 'create_live_event_clicked',
  EventsCreateFlowStarted = 'schedule_broadcast_cta_clicked',
  EventsDetailsFormDiscoverableLearnMoreLinkClicked = 'events_details_form_discoverable_learn_more_link_clicked',
  EventsGroupFilterSelected = 'events_group_filter_selected',
  EventsGroupPageLoad = 'events_group_events_page_load',
  EventsPageLoaded = 'visit_broadcast_page',
  EventsPresentLinkClicked = 'events_present_link_clicked',
  EventsProduceLinkClicked = 'events_produce_link_clicked',
  EventsProductionInstructionsHelpLinkClicked = 'events_production_instructions_help_link_clicked',
  EventsSkipGroupCalendarInviteClicked = 'events_skip_group_calendar_invite_clicked',
  ExperimentTreatment = 'experiment_treatment',
  ExternalLinkClicked = 'external_link_clicked',
  FaqifyDeleteClicked = 'faqify_delete_clicked',
  FaqifyEditClicked = 'faqify_edit_clicked',
  FaqifyFREDisplayed = 'faqify_fre_displayed',
  FaqifyFREDismissed = 'faqify_fre_dismissed',
  FaqifyUploadButtonClicked = 'faqify_upload_button_clicked',
  FavoriteModified = 'favorite_modified',
  FeedbackFormOpened = 'feedback_form_opened',
  FeedbackFormSubmitted = 'feedback_form_submitted',
  FeedCacheHit = 'feed_cache_hit',
  FeedFilterOpened = 'feed_filter_opened',
  FeedFilterSelected = 'feed_filter_selected',
  FeedHovered = 'feed_hovered',
  FeedLayoutPicked = 'feed_layout_picked',
  FeedLinkClicked = 'feed_link_clicked',
  FeedLoaded = 'feed_loaded',
  FeedManualLoadMore = 'feed_manual_load_more',
  FeedSubtypePicked = 'feed_subtype_picked',
  FeedThreadGridClicked = 'feed_thread_grid_clicked',
  FirstRunExperienceDismissed = 'first_run_experience_dismissed',
  FloodgateInitialized = 'floodgate_initialized',
  FloodgateSurveyActivated = 'floodgate_survey_activated',
  FloodgateSurveyDismissed = 'floodgate_survey_dismissed',
  FloodgateSurveySubmitted = 'floodgate_survey_submitted',
  FollowStorylineButtonClicked = 'follow_storyline_button_clicked',
  FollowStorylineEmailCheckboxClicked = 'follow_storyline_email_notification_set',
  FollowStorylineTeamsCheckboxClicked = 'follow_storyline_teams_notification_set',
  FollowStorylineYammerCheckboxClicked = 'follow_storyline_yammer_notification_set',
  FollowUsersLightboxShown = 'follow_users_lightbox_shown',
  GifPickerClicked = 'gif_picker_clicked',
  GoToYammerClicked = 'go_to_yammer_clicked',
  GoaltrackerAddButtonClicked = 'goaltracker_add_button_clicked',
  GoaltrackerNextStepButtonClicked = 'goaltracker_next_step_button_clicked',
  GoaltrackerDeleteButtonClicked = 'goaltracker_delete_button_clicked',
  GoaltrackerLogProgressButtonClicked = 'goaltracker_log_progress_button_clicked',
  GoaltrackerSaveButtonClicked = 'goaltracker_save_button_clicked',
  GoaltrackerEditButtonClicked = 'goaltracker_edit_button_clicked',
  GoaltrackerConfirmAddGoalClicked = 'goaltracker_confirm_add_goal_button_clicked',
  GoaltrackerConfirmEditGoalClicked = 'goaltracker_confirm_edit_goal_button_clicked',
  GoaltrackerAddGoalModalDismissed = 'goaltracker_add_goal_modal_dismissed',
  GoaltrackerSetGoalModalDismissed = 'goaltracker_set_goal_modal_dismissed',
  GoaltrackerLogProgressModalDismissed = 'goaltracker_log_progress_modal_dismissed',
  GroupAboutPageLoaded = 'group_about_page_loaded',
  GroupAdminSubscriptionTipDismissed = 'group_admin_subscription_tip_dismissed',
  GroupAdminSubscriptionTipDisplayed = 'group_admin_subscription_tip_displayed',
  GroupAdminSubscriptionTipRemindLater = 'group_admin_subscription_tip_remind_later',
  GroupAdminSubscriptionTipSubscribeClicked = 'group_admin_subscription_tip_subscribe_clicked',
  GroupAvatarUpdated = 'group_avatar_updated',
  GroupCardDisplayed = 'group_card_displayed',
  GroupClicked = 'group_clicked',
  GroupCopyLinkToClipboardClicked = 'group_copy_link_to_clipboard_clicked',
  GroupInsightsLinkClicked = 'group_insights_link_clicked',
  GroupJoinButtonClicked = 'group_join_button_clicked',
  GroupLeaveButtonClicked = 'group_leave_button_clicked',
  GroupMemberPanelActivated = 'group_member_panel_activated',
  GroupMemberPanelClosed = 'group_member_panel_closed',
  GroupMemberPanelOpened = 'group_member_panel_opened',
  GroupMemberPanelSearch = 'group_member_panel_search',
  GroupMembersCSVImportClicked = 'group_member_csv_import_clicked',
  GroupMembersCSVImportUploadAttempted = 'group_member_csv_import_upload_attempt',
  GroupMembersCSVImportUploadSucceeded = 'group_member_csv_import_upload_succeeded',
  GroupMembersCSVImportUploadFailed = 'group_member_csv_import_upload_failed',
  GroupMembersCSVImportAbortClicked = 'group_member_csv_import_stop_clicked',
  GroupMemberPanelShowMoreClicked = 'group_member_panel_show_more_clicked',
  GroupOverFlowMenuClicked = 'group_over_flow_menu_clicked',
  GroupOverFlowMenuItemClicked = 'group_over_flow_menu_item_clicked',
  GroupPinnedResourcesActivated = 'group_pinned_resources_activated',
  GroupSettingsPostingPermissionsChanged = 'group_settings_posting_permissions_changed',
  GroupSubscriptionButtonClicked = 'group_subscription_button_clicked',
  GroupSubscriptionTeachingBubbleDismissed = 'group_subscription_teaching_bubble_dismissed',
  GroupSubscriptionTeachingBubbleDisplayed = 'group_subscription_teaching_bubble_displayed',
  GroupSuggestedMemberDisplayed = 'group_suggested_members_displayed',
  GroupSuggestedMemberAdded = 'group_suggested_members_added',
  GroupSuggestedMemberDismissed = 'group_suggested_members_dismissed',
  GroupSuggestionHovered = 'group_suggestion_hovered',
  GroupSuggestionRejected = 'group_suggestion_rejected',
  GroupSettingsPageLoaded = 'group_settings_page_loaded',
  GroupTeachingBubbleClicked = 'group_teaching_bubble_clicked',
  GroupTeachingBubbleDismissed = 'group_teaching_bubble_dismissed',
  GroupTeachingBubbleDisplayed = 'group_teaching_bubble_displayed',
  HashTagClicked = 'hashtag_clicked',
  HomeFeedNextPageRequested = 'home_feed_next_page_requested',
  HomeTabClicked = 'home_tab_clicked',
  ImageLoaded = 'image_loaded',
  InboxLinkClicked = 'inbox_link_clicked',
  InboxNavigationClicked = 'inbox_navigation_clicked',
  CampaignsDashboardNavigationClicked = 'campaigns_dashboard_navigation_clicked',
  CampaignsDashboardCampaignClicked = 'campaigns_dashboard_campaign_clicked',
  CampaignPinnedResourcesActivated = 'campaign_pinned_resources_activated',
  CampaignCoverImageModified = 'campaign_cover_image_modified',
  InclusiveReactionsFirstRunExperienceChangeSkinToneClicked = 'inclusive_reactions_fre_change_skin_tone_clicked',
  InclusiveReactionsFirstRunExperienceDismissed = 'inclusive_reactions_fre_dismissed',
  InclusiveReactionsFirstRunExperienceOpened = 'inclusive_reactions_fre_opened',
  InclusiveReactionsReactionUsed = 'inclusive_reactions_reaction_used',
  InclusiveReactionsSettingsMenuLinkClicked = 'inclusive_reactions_settings_menu_link_clicked',
  InclusiveReactionsSkinToneChanged = 'inclusive_reactions_skin_tone_changed',
  LeadershipCornerLinkClicked = 'leadership_corner_link_clicked',
  LeadershipCornerTabClicked = 'leadership_corner_tab_clicked',
  LeftNavCreateGroupClicked = 'left_nav_create_a_group_clicked',
  LinkPreviewGenerated = 'link_preview_generated',
  LiveInsightsEngagementViewClicked = 'live_insights_engagement_view_clicked',
  LiveInsightsReachViewClicked = 'live_insights_reach_view_clicked',
  LiveInsightsSeeAllClicked = 'live_insights_see_all_clicked',
  LoadMoreDiscoveryBannersClicked = 'load_more_discovery_banners_clicked',
  MessageBodyCollapsed = 'message_body_collapsed',
  MessageBodyExpanded = 'message_body_expanded',
  MessageDeleteSelected = 'message_delete_selected',
  MessageEditSelected = 'message_edit_selected',
  MessageReplySelected = 'message_reply_selected',
  MobileUpsellBannerClicked = 'mobile_upsell_banner_clicked',
  MobileUpsellBannerDismissed = 'mobile_upsell_banner_dismissed',
  MobileUpsellBannerQrCodeSeen = 'mobile_upsell_banner_qrcode_seen',
  MobileUpsellBannerSeen = 'mobile_upsell_banner_seen',
  MobileCrossSellQRCodeCardSeen = 'mobile_crosssell_qrcode_promotion_displayed',
  MobileCrossSellQRCodeCardDismissed = 'mobile_crosssell_qrcode_promotion_dismissed',
  MobileAppPromotionSeen = 'mobile_promotion_displayed',
  MobileAppPromotionClicked = 'mobile_promotion_tapped',
  MobileAppPromotionDismissed = 'mobile_promotion_dismissed',
  ModalClosed = 'modal_closed',
  ModalOpened = 'modal_opened',
  MomentOpened = 'moment_opened',
  MomentsCollapsibleEvent = 'moments_collapsible_event',
  MomentsGAToggled = 'moment_ga_toggled',
  MomentsPublicPreviewToggled = 'moment_public_preview_toggled',
  NavigatedToMessage = 'navigated_to_message',
  NetworkFromSwitcherClicked = 'network_from_switcher_clicked',
  NetworkSwitcherPanelOpened = 'network_switcher_panel_opened',
  NewReplyPillClicked = 'new_reply_pill_clicked',
  NewThreadPillClicked = 'new_thread_pill_clicked',
  NextGroupCTAClicked = 'next_group_cta_clicked',
  NotificationClicked = 'notification_clicked',
  NotificationDisplayed = 'notification_displayed',
  NotificationFollowedByUserModalClicked = 'notification_followed_by_user_modal_clicked',
  NotificationFollowedByUserModalDismissed = 'notification_followed_by_user_modal_dismissed',
  NotificationsCountDisplayed = 'notifications_count_displayed',
  NotificationsPanelOpened = 'notifications_panel_opened',
  OfficeOneNoteLinkClicked = 'office_one_note_link_clicked',
  OfficePlannerLinkClicked = 'office_planner_link_clicked',
  OfficeSharePointDocumentLibraryLinkClicked = 'office_sharepoint_document_library_link_clicked',
  OfficeSharePointSiteLinkClicked = 'office_sharepoint_site_link_clicked',
  OfficialCommunityBadgeDisplayed = 'official_community_badge_displayed',
  OfficialCommunityBadgeHovered = 'official_community_badge_hovereded',
  OpenPublisherButtonClicked = 'open_publisher_button_clicked',
  OnePlayerLoaded = 'one_player_loaded',
  OutlookEngageRebrandingBannerDismissed = 'outlook_engage_rebranding_banner_dismissed',
  OutlookAnswersBannerDismissed = 'outlook_answers_banner_dismissed',
  OutlookGoToYammerClicked = 'outlook_go_to_yammer_clicked',
  OWAThreadViewConfigured = 'owa_threadview_configured',
  OwnProfileLinkClicked = 'own_profile_link_clicked',
  PageLoadedFromLinkClick = 'page_loaded_from_link_click',
  PeoplePageClicked = 'people_page_clicked',
  PinnedThreadFullyCollapsed = 'pinned_thread_fully_collapsed',
  PinnedThreadFullyExpanded = 'pinned_thread_fully_expanded',
  PopUpLightBoxDismissed = 'pop_up_lightbox_dismissed',
  PopUpLightBoxDisplayed = 'pop_up_lightbox_displayed',
  PostDropDownClicked = 'post_dropdown_clicked',
  PostingError = 'posting_error',
  PostNewReply = 'post_new_reply',
  PostNewThreadStarter = 'post_new_thread_starter',
  PremiumPreviewFeaturesToggled = 'premium_preview_features_toggled',
  ProfileLinkClicked = 'profile_link_clicked',
  PublisherAddTopicClicked = 'publisher_add_topic_clicked',
  PublisherCollapsed = 'publisher_collapsed',
  PublisherDismissed = 'publisher_dismissed',
  PublisherExpandedDropdownClicked = 'publisher_expanded_dropdown_clicked',
  PublisherExpandedFormattingClicked = 'publisher_expanded_formatting_clicked',
  PublisherGifPosted = 'publisher_gif_posted',
  PublisherGuideFREDisplayed = 'publisher_guide_fre_displayed',
  PublisherGuideFREClosed = 'publisher_guide_fre_closed',
  PublisherMessageDrafted = 'publisher_message_drafted',
  PublisherMessageScheduled = 'publisher_message_scheduled',
  PublisherMessagePosted = 'publisher_message_posted',
  PublisherNotifiedUserAdded = 'publisher_notified_user_added',
  PublisherNotifiedUserRemoved = 'publisher_notified_user_removed',
  PublisherOpened = 'publisher_opened',
  PublisherCancelled = 'publisher_cancelled',
  PublisherTopicBarButtonClicked = 'publisher_topic_bar_button_clicked',
  PublisherTopicAdded = 'publisher_topic_added',
  PublisherTopicRemoved = 'publisher_topic_removed',
  PushNotificationsNotificationClicked = 'push_notifications_notification_clicked',
  PushNotificationsNotificationShown = 'push_notifications_notification_shown',
  PushNotificationsSettingsBrowserPermissionGranted = 'push_notifications_settings_browser_permission_granted',
  PushNotificationsSettingsBrowserPermissionRequested = 'push_notifications_settings_browser_permission_requested',
  PushNotificationsSettingsSaved = 'push_notifications_settings_saved',
  PushNotificationsTeachingBubbleClicked = 'push_notifications_teaching_bubble_clicked',
  PushNotificationsTeachingBubbleOpened = 'push_notifications_teaching_bubble_opened',
  PWAInstallTeachingBubbleClicked = 'pwa_install_teaching_bubble_clicked',
  PWAInstallTeachingBubbleOpened = 'pwa_install_teaching_bubble_opened',
  PWAInstallTeachingBubbleSucceeded = 'pwa_install_teaching_bubble_succeeded',
  PWAStandaloneAppLaunched = 'pwa_standalone_app_launched',
  QAListAnswerQuestionClicked = 'qa_list_answer_question_clicked',
  QAListQuestionClicked = 'qa_list_question_clicked',
  QAListSeeMoreAnswerClicked = 'qa_list_see_more_answer_clicked',
  QAListViewAllAnswersClicked = 'qa_list_view_all_answers_clicked',
  QAndAAppAdded = 'qanda_app_added',
  OfficeTopicsDisabled = 'office_topics_disabled',
  QuestionsInfoPanelHyperlinkClicked = 'info_panel_hyperlink_clicked',
  QuestionsInfoPanelEditClicked = 'info_panel_edit_clicked',
  QuestionsInfoPanelAddGuidelinesClicked = 'info_panel_add_guidelines_clicked',
  QuestionsInfoPanelEdited = 'info_panel_edited',
  QuestionsInfoPanelReset = 'info_panel_reset',
  QuestionsOnboardingCollapseClicked = 'questions_onboarding_collapse_clicked',
  QuestionsOnboardingDismissClicked = 'questions_onboarding_dismiss_clicked',
  QuestionsOnboardingDisplayed = 'questions_onboarding_displayed',
  QuestionsOnboardingExpandClicked = 'questions_onboarding_expand_clicked',
  QuestionsOnboardingUpvoteBubbleDisplayed = 'questions_onboarding_upvote_bubble_displayed',
  QuestionsOnboardingUpvoteBubbleDismissed = 'questions_onboarding_upvote_bubble_dismissed',
  ReachButtonClicked = 'reach_button_clicked',
  ReactionClicked = 'reaction_clicked',
  ReactionSelectorOpened = 'reaction_selector_opened',
  RealtimeReactionEventReceived = 'realtime_reaction_event_received',
  RelatedGroupAdded = 'related_group_added',
  RelatedGroupClicked = 'related_group_clicked',
  RelatedGroupRemoved = 'related_group_removed',
  RepliesLoadMoreClicked = 'replies_load_more_clicked',
  RestrictedGroupLearnMoreClicked = 'restricted_group_learn_more_clicked',
  RichTextFormattingApplied = 'rich_text_formatting_applied',
  RightPanelExpanded = 'right_panel_expanded',
  SearchTabClicked = 'search_tab_clicked',
  StorylineAnnouncementTeachingModalDismissedClicked = 'storyline_announcement_teaching_modal_dismissed_clicked',
  StorylineAnnouncementTeachingModalTriggered = 'storyline_announcement_teaching_modal_triggered',
  StorylineAnnouncementTeachingModalTryItClicked = 'storyline_announcement_teaching_modal_try_it_clicked',
  StorylineDigestTeachingBubbleDismissed = 'storyline_digest_teaching_bubble_dismissed',
  StorylineDigestTeachingBubbleDisplayed = 'storyline_digest_teaching_bubble_displayed',
  StorylineEnableToggled = 'storyline_enable_toggled',
  StorylineGAToggled = 'storyline_ga_toggled',
  StorylineHomeClicked = 'storylines_feed_home_clicked',
  StorylineHomeFeedLinkClicked = 'storylines_feed_link_clicked',
  StorylineLearnMoreClicked = 'storyline_learn_more_clicked',
  StorylineFREIsDisplayed = 'storyline_frelb_displayed',
  StorylineFREIntroTeachingBubbleDismissButtonClicked = 'storyline_frelb_later_clicked',
  StorylineFREIntroTeachingBubbleContinueButtonClicked = 'storyline_frelb_more_clicked',
  StorylineFREIntroTeachingBubbleCloseButtonClicked = 'storyline_frelb_closed',
  StorylineFRENextButtonClicked = 'storyline_fre_next_clicked',
  StorylineFREPreviousButtonClicked = 'storyline_fre_prev_clicked',
  StorylineFRECongratulationsTeachingBubbleDisplayed = 'storyline_congratslb_displayed',
  StorylineFRECongratulationsTeachingBubbleDismissButtonClicked = 'storyline_congratslb_gotit_clicked',
  StorylineFRECongratulationsTeachingBubbleContinueButtonClicked = 'storyline_congratslb_checkit_clicked',
  StorylineFRECongratulationsTeachingBubbleClosed = 'storyline_congratslb_closed',
  StorylinePostFromOutlookCompleted = 'storyline_post_from_outlook_completed',
  StorylinePostFromOutlookTipsClicked = 'storyline_post_from_outlook_tips_clicked',
  StorylinesTabClicked = 'storylines_tab_clicked',
  StorylineNotificationDefaultsSaved = 'storyline_notification_defaults_saved',
  StorylineCreationControlsSaved = 'storyline_creation_controls_saved',
  SuggestedGroupClicked = 'suggested_group_clicked',
  SuggestNewTopicClicked = 'suggest_new_topic_clicked',
  SuggestedTopicHover = 'suggested_topic_hover',
  SuggestedTopicIconHover = 'suggested_topic_icon_hover',
  SearchBegin = 'search_begin',
  TeamsAppLoadedFromCache = 'teams_app_loaded_from_cache',
  TeamsBackButtonClicked = 'teams_back_button_clicked',
  TeamsDeepLinkClicked = 'teams_deep_link_clicked',
  TeamsMeetingDeepLinkClicked = 'teams_meeting_deep_link_clicked',
  TeamsQnASettingsModified = 'teams_qa_settings_modified',
  TeamsQnANotSetupPageOpened = 'teams_qa_not_setup_page_opened',
  TeamsQnAThreadViewLoaded = 'teams_qa_thread_view_loaded',
  TeamsSuggestToPinDismissed = 'teams_suggest_to_pin_dismissed',
  TestUsageEvent = 'test_usage_event',
  ThreadBestReplyMarked = 'thread_best_reply_marked',
  ThreadCommentsCollapsed = 'thread_comments_collapsed',
  ThreadDeleteSelected = 'thread_delete_selected',
  ThreadFollowed = 'thread_followed',
  ThreadLinkCopied = 'thread_link_copied',
  ThreadLoadOlderRepliesSelected = 'thread_load_older_replies_selected',
  ThreadPinned = 'thread_pinned',
  ThreadUnpinned = 'thread_unpinned',
  ThreadPromoted = 'thread_promoted',
  ThreadReportCanceled = 'thread_report_canceled',
  ThreadReportConfirmed = 'thread_report_confirmed',
  ThreadReportSelected = 'thread_report_selected',
  ThreadSharesCountClicked = 'thread_shares_count_clicked',
  ThreadShowCommentsSelected = 'thread_show_comments_selected',
  ThreadStarterAttachmentExpanded = 'thread_starter_attachments_expanded',
  ThreadUnfollowed = 'thread_unfollowed',
  ThreadUnpromoted = 'thread_unpromoted',
  ThreadViewConversationSelected = 'thread_view_conversation_selected',
  TopicAddedToPublisher = 'topic_added_to_publisher',
  TopicAddedToThread = 'topic_added_to_thread',
  TopicApplyToThreadClicked = 'topic_apply_to_thread_clicked',
  TopicCardInvoked = 'topic_card_invoked',
  TopicClicked = 'topic_clicked',
  TopicCreated = 'topic_created',
  TopicCreateFlowCompleted = 'topic_create_flow_completed',
  TopicCreateFlowDismissed = 'topic_create_flow_dismissed',
  TopicEditClicked = 'topic_edit_clicked',
  TopicEditDismissed = 'topic_edit_dismissed',
  TopicEdited = 'topic_edited',
  TopicEditLinkClicked = 'topic_edit_link_clicked',
  TopicFollowed = 'topic_followed',
  TopicFollowersPanelClosed = 'topic_followers_panel_closed',
  TopicFollowersPanelOpened = 'topic_followers_panel_opened',
  TopicLoadMoreClick = 'topic_load_more_clicked',
  TopicManagementLinkClicked = 'topic_management_link_clicked',
  TopicPickerTopicSelected = 'topic_picker_topic_selected',
  TopicRemovedFromPublisher = 'topic_removed_from_publisher',
  TopicRemovedFromThread = 'topic_removed_from_thread',
  TopicReviewClicked = 'topic_review_clicked',
  TopicReviewFeatureCompleted = 'topic_review_feature_completed',
  TopicReviewIgnoreCompleted = 'topic_review_ignore_completed',
  TopicSuggestFlowCompleted = 'topic_suggest_flow_completed',
  TopicSuggestFlowDismissed = 'topic_suggest_flow_dismissed',
  TopicUnfollowed = 'topic_unfollowed',
  TrendingTopicClicked = 'trending_topic_clicked',
  TypeaheadAbandoned = 'typeahead_abandoned',
  TypeaheadSuggestionSelected = 'typeahead_suggestion_selected',
  TypingPublished = 'typing_published',
  UnbookmarkClicked = 'unbookmark_clicked',
  UnfollowStorylineButtonClicked = 'unfollow_storyline_button_clicked',
  UnfollowStorylineEmailCheckboxClicked = 'unfollow_storyline_email_notification_set',
  UnfollowStorylineTeamsCheckboxClicked = 'unfollow_storyline_teams_notification_set',
  UnfollowStorylineYammerCheckboxClicked = 'unfollow_storyline_yammer_notification_set',
  UpdateOnBehalfOfSenderLearnMoreClicked = 'update_on_behalf_of_sender_learn_more_clicked',
  UploadFileClicked = 'upload_file_clicked',
  UploadImageClicked = 'upload_image_clicked',
  UploadMediaClicked = 'upload_media_clicked',
  UserAvatarMediaObjectClicked = 'user_avatar_media_object_clicked',
  UserCoverImageModified = 'user_cover_image_modified',
  UserFollowClicked = 'user_follow_clicked',
  UserOptedin = 'user_optedin',
  UserProfileClicked = 'user_profile_clicked',
  UserSuggestionFollowClicked = 'user_profile_follow_clicked',
  UserSuggestionRejected = 'user_suggestion_rejected',
  UserSuggestionUnfollowClicked = 'user_profile_unfollow_clicked',
  LeaderSuggestionFollowClicked = 'leader_suggestion_follow_clicked',
  LeaderSuggestionRejected = 'leader_suggestion_rejected',
  LeaderSuggestionUnfollowClicked = 'leader_suggestion_unfollow_clicked',
  UserUnfollowClicked = 'user_unfollow_clicked',
  UserAdminRolesChanged = 'user_admin_roles_changed',
  ViewAllCommunitiesClicked = 'view_all_communities_clicked',
  ViewAllInYammer = 'view_all_in_yammer',
  ViewFullConversationClicked = 'view_full_conversation_clicked',
  ViewPostInYammerClicked = 'view_post_in_yammer_clicked',
  ViewVotedUserList = 'view_voted_user_list',
  VivaEngageIconClicked = 'viva_engage_icon_clicked',
  VivaEngageBackButtonClicked = 'viva_engage_back_button_clicked',
  VivaSuiteNavPanelClosed = 'viva_suite_nav_panel_closed',
  VivaSuiteNavPanelOpened = 'viva_suite_nav_panel_opened',
  VivaSuiteNavFlyoutClosed = 'viva_suite_nav_flyout_closed',
  VivaSuiteNavFlyoutOpened = 'viva_suite_nav_flyout_opened',
  VivaSuiteNavLinkClicked = 'viva_suite_nav_link_clicked',
  VivaTopicsFREDisplayed = 'viva_topics_fre_displayed',
  VivaTopicsFREClicked = 'viva_topics_fre_clicked',
  WebLinkCardClicked = 'web_link_card_clicked',
  WebpartConfigured = 'webpart_configured',
  WebRecorderClicked = 'webrecorder_clicked',
  WebRecorderOpened = 'webrecorder_opened',
  WebRecorderRecordingSessionCompleted = 'webrecorder_recording_session_completed',
}
